'use client';

/**
 * API namespace `conecta` — CONECTA-PR3-FE-HUB-OPT-IN.
 *
 * Wraps the 13 endpoints under /api/conecta/* (PR2 backend).
 *
 * Public (no auth — visitors + the Hub settings page both use these):
 *   GET  /conecta/listings                       → getListings()
 *   GET  /conecta/listings/:username             → getListing()
 *   GET  /conecta/listings/:username/reviews     → getReviews()
 *   GET  /conecta/categories                     → getCategories()
 *   GET  /conecta/locations/countries            → getCountries()
 *   GET  /conecta/locations/cities/:countryCode  → getCities()
 *
 * Auth (requireAuth — cookie-based JWT, apiFetch handles it):
 *   GET    /conecta/me/listing                   → getMyListing()
 *   POST   /conecta/me/opt-in                     → optIn()
 *   PATCH  /conecta/me/listing                    → updateListing()
 *   DELETE /conecta/me/opt-out                    → optOut()
 *   POST   /conecta/me/boost                      → boost()            [501 stub — PR6 implements]
 *   POST   /conecta/categories/suggest            → suggestCategory()
 *   POST   /conecta/listings/:username/reviews    → createReview()     [501 stub — PR5 implements]
 *
 * Anti-§6 (CM-19/CM-15): pure thin wrapper over `apiFetch`. NO business
 * logic. The backend controllers (`conecta-public.controller.ts` +
 * `conecta-auth.controller.ts`) are the source of truth for response
 * shapes — DTOs here mirror them VERBATIM.
 *
 * Anti-P-041: 0 NEW `as any` in this file.
 * Anti-P-042: 0 NEW `console.*`.
 * Anti-P-013: errors propagate as `ApiClientError` (thrown by `apiFetch`).
 *             The 404 anti-enumeration cases are caught + converted to null
 *             (the BE returns the SAME 404 for "not found" AND "hidden"
 *             per SPEC §14.2 — the FE treats both as "no listing" so the
 *             UI doesn't leak existence).
 * Anti-P-347: VERBATIM mirror of `auto-withdraw.ts` namespace pattern
 *             (`'use client'` + `apiFetch<T>` returns `T` (auto-unwrap
 *             of `{ data: T }` envelope is built-in to `apiFetch`) +
 *             typed DTOs + named const exports).
 * Anti-P-382: thin wrapper — NO facade, NO service-layer delegation, NO
 *             parallel mechanism on the FE.
 * Anti-P-383: file ≤500L target.
 *
 * Spec ref: docs/SPEC-245-PROFILEIA-CONECTA.md §5 (endpoints), §6 (opt-in
 *           flow + 6 eligibility gates), §8 (categories hybrid), §9
 *           (location 3 levels), §13 (business rules), §14.2 (anti-
 *           enumeration).
 *           Task ID: CONECTA-PR3-FE-HUB-OPT-IN.
 */

import { apiFetch } from '../core/fetch-client';
import { ApiClientError } from '../core/error-handler';

// ─── Types (mirror backend conecta-public.controller.ts + conecta-auth.controller.ts) ───

/**
 * Approved Conecta category (mirrors backend `ConectaCategoryView` from
 * `category.service.ts`).
 *
 * TASK-FEAT-CONECTA-ADMIN-CREATE-CATEGORIES: added `subcategories` field
 * (default [] for backward compat with the 49 seed categories which are
 * flat — pre-date this field).
 */
export interface ConectaCategory {
  categoryId: string;
  slug: string;
  name: string;
  description: string;
  icon: string;
  vertical: string;
  /** Subcategory display labels (default [] — flat categories have none). */
  subcategories: string[];
  /** Denormalized count of active listings in this category. */
  listingCount: number;
}

/** Card shape returned by GET /conecta/listings (mirrors `ListingCardView`). */
export interface ConectaListingCard {
  listingId: string;
  username: string;
  displayName: string;
  bio: string;
  photoUrl: string;
  primaryCategoryId: string;
  locationScope: 'global' | 'country' | 'city';
  country: string | null;
  city: string | null;
  isBoosted: boolean;
  boostExpiresAt: string | null;
  reviewCount: number;
  averageRating: number;
}

/** Paginated response shape returned by GET /conecta/listings. */
export interface ConectaListingsResponse {
  listings: ConectaListingCard[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  filters: {
    categories: Array<{
      categoryId: string;
      slug: string;
      name: string;
      vertical: string;
      listingCount: number;
    }>;
    countries: string[];
    cities: string[];
  };
}

/** Detail shape returned by GET /conecta/listings/:username + GET /me/listing. */
export interface ConectaListingDetail extends ConectaListingCard {
  categoryIds: string[];
  profileSlug: string;
  profileViews: number;
  /**
   * Internal status of the listing. Returned by GET /me/listing (owner read)
   * so the FE Hub settings page can derive `'active' | 'paused_by_user' |
   * 'not_created'` for the StatusCard display. NOT returned by the public
   * read path `GET /conecta/listings/:username` (anti-enumeration: returns
   * 404 for non-`'active'` listings).
   *
   * Anti-P-382 fix (2026-10-27): the BE used to omit this field from
   * `getMyListing()` even though `.select()` included it. The PR #1464
   * toggle UI exposed the bug — every active listing was rendered as
   * "paused" + the toggle called `optIn` (instead of `optOut`) → BE 409.
   * Marked optional because the public read path legitimately omits it.
   */
  status?: 'active' | 'hidden' | 'paused_by_user' | 'auto_hidden_by_reports';
}

/** Country or city option (mirrors backend `GeoLocationOption` shape). */
export interface ConectaLocation {
  code: string;
  name: string;
}

/** Review shape returned by GET /conecta/listings/:username/reviews. */
export interface ConectaReview {
  reviewId: string;
  rating: number;
  title: string;
  comment: string;
  createdAt: string;
}

/** Paginated reviews response. */
export interface ConectaReviewsResponse {
  reviews: ConectaReview[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  averageRating: number;
}

/** POST /me/opt-in body (mirrors backend `OptInBody`). */
export interface OptInRequest {
  /** 1-5 categoryIds (SPEC §8.3). */
  categoryIds: string[];
  locationScope: 'global' | 'country' | 'city';
  /** ISO 3166-1 alpha-2 (required when scope !== 'global'). */
  country?: string;
  city?: string;
}

/** PATCH /me/listing body (mirrors backend `UpdateListingBody`). All optional. */
export interface UpdateListingRequest {
  bio?: string;
  categoryIds?: string[];
  locationScope?: 'global' | 'country' | 'city';
  country?: string | null;
  city?: string | null;
}

/** POST /categories/suggest body (mirrors backend `SuggestCategoryBody`). */
export interface SuggestCategoryRequest {
  name: string;
  description?: string;
  icon?: string;
  vertical: string;
}

/** POST /categories/suggest response (mirrors BE service return). */
export interface SuggestCategoryResponse {
  categoryId: string;
  slug: string;
  status: 'pending';
  message: string;
}

/** DELETE /me/opt-out response. */
export interface OptOutResponse {
  ok: true;
  status: 'paused_by_user';
}

/**
 * 501 stub response (PR6 boost — kept for backward compat with FE
 * code that may still type-check against the old `NotImplementedStub`
 * shape; the actual `boost()` method now returns `ConectaListingDetail`
 * per the PR6 implementation above).
 */
export interface NotImplementedStub {
  error: string;
  pr: string;
}

/**
 * Review view (mirrors backend `ReviewView` from `review-helpers.ts`).
 * Returned by createReview + updateReview. Includes the moderation
 * status + flags so the FE can show "pendiente de revisión" feedback
 * when the local wordlist flags the review.
 */
export interface ConectaReviewView {
  reviewId: string;
  listingId: string;
  rating: number;
  title: string;
  comment: string;
  status: 'published' | 'hidden_by_admin' | 'pending_ai_review';
  moderationFlags: {
    flaggedByWordlist: boolean;
    flaggedByAI: boolean;
    aiVerdict?: 'safe' | 'borderline' | 'toxic';
    hiddenAt?: string;
    hiddenByAdminId?: string;
    hiddenReason?: string;
  };
  createdAt: string;
  updatedAt: string;
}

/** POST /listings/:username/reviews body. rating 1-5, title max 100, comment max 1000. */
export interface CreateReviewRequest {
  rating: number;
  title: string;
  comment: string;
}

/** PATCH /reviews/:reviewId body. All fields optional. */
export interface UpdateReviewRequest {
  rating?: number;
  title?: string;
  comment?: string;
}

/** Soft-delete own review response. */
export interface DeleteReviewResponse {
  ok: true;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Returns true if the given error is an `ApiClientError` with a 404 status
 * (the backend's anti-enumeration response for both "not found" AND "hidden"
 * listings per SPEC §14.2). The FE treats both as "no listing" so the UI
 * doesn't leak existence.
 */
function isNotFound(err: unknown): boolean {
  return err instanceof ApiClientError && err.status === 404;
}

// ─── Public endpoints (no auth — also used by the Hub settings page) ─────────

export const conectaPublic = {
  /**
   * GET /conecta/listings — paginated public listings with filters + search
   * + sorting. Cached 60s server-side (Redis) per SPEC §7.6.
   */
  async getListings(params?: {
    page?: number;
    pageSize?: number;
    categoryId?: string;
    locationScope?: 'global' | 'country' | 'city';
    country?: string;
    city?: string;
    q?: string;
    sort?: 'default' | 'rating';
  }): Promise<ConectaListingsResponse> {
    const search = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') {
          search.append(k, String(v));
        }
      });
    }
    const qs = search.toString() ? `?${search.toString()}` : '';
    return apiFetch<ConectaListingsResponse>(`/conecta/listings${qs}`);
  },

  /**
   * GET /conecta/listings/:username — listing detail. Anti-enumeration:
   * returns null for both "not found" AND "hidden" (SPEC §14.2).
   */
  async getListing(username: string): Promise<ConectaListingDetail | null> {
    try {
      return await apiFetch<ConectaListingDetail>(
        `/conecta/listings/${encodeURIComponent(username)}`,
      );
    } catch (err) {
      if (isNotFound(err)) return null;
      throw err;
    }
  },

  /**
   * GET /conecta/listings/:username/reviews — published reviews for a
   * listing. Anti-enumeration: returns null for "not found" AND "hidden".
   */
  async getReviews(
    username: string,
    page = 1,
    pageSize = 10,
  ): Promise<ConectaReviewsResponse | null> {
    try {
      return await apiFetch<ConectaReviewsResponse>(
        `/conecta/listings/${encodeURIComponent(username)}/reviews?page=${page}&pageSize=${pageSize}`,
      );
    } catch (err) {
      if (isNotFound(err)) return null;
      throw err;
    }
  },

  /** GET /conecta/categories — approved categories with listingCount. Cached 1h. */
  async getCategories(): Promise<ConectaCategory[]> {
    return apiFetch<ConectaCategory[]>(`/conecta/categories`);
  },

  /** GET /conecta/locations/countries — active countries from latam-geo seed. */
  async getCountries(): Promise<ConectaLocation[]> {
    return apiFetch<ConectaLocation[]>(`/conecta/locations/countries`);
  },

  /** GET /conecta/locations/cities/:countryCode — cities of a country. */
  async getCities(countryCode: string): Promise<ConectaLocation[]> {
    return apiFetch<ConectaLocation[]>(
      `/conecta/locations/cities/${encodeURIComponent(countryCode)}`,
    );
  },
};

// ─── Auth endpoints (require JWT via cookie — apiFetch handles it) ──────────

export const conectaAuth = {
  /**
   * GET /conecta/me/listing — the authed user's own listing (any status —
   * owner can see their paused/hidden listing). Returns null when the user
   * has no listing yet (BE returns 404 LISTING_NOT_FOUND for the opt-in CTA).
   */
  async getMyListing(): Promise<ConectaListingDetail | null> {
    try {
      return await apiFetch<ConectaListingDetail>(`/conecta/me/listing`);
    } catch (err) {
      if (isNotFound(err)) return null;
      throw err;
    }
  },

  /**
   * POST /conecta/me/opt-in — activate Conecta. Runs the 6 eligibility
   * gates (KYC L2 → 403, existing listing → 409, bio → 400, photo → 400,
   * categories 1-5 → 400, location → 400) + 24h cooldown (429) per SPEC §6.
   *
   * On 201, returns the freshly created listing detail view.
   */
  async optIn(body: OptInRequest): Promise<ConectaListingDetail> {
    return apiFetch<ConectaListingDetail>(`/conecta/me/opt-in`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /**
   * PATCH /conecta/me/listing — partial update of bio + categories +
   * location + snapshot refresh. Returns the updated listing detail.
   */
  async updateListing(
    body: UpdateListingRequest,
  ): Promise<ConectaListingDetail> {
    return apiFetch<ConectaListingDetail>(`/conecta/me/listing`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  /**
   * DELETE /conecta/me/opt-out — pause the listing (status='paused_by_user').
   * Preserves all data; re-activation is an upsert, NOT a re-create (§6.4).
   * The 24h cooldown starts at this moment (§13 rule 8).
   */
  async optOut(): Promise<OptOutResponse> {
    return apiFetch<OptOutResponse>(`/conecta/me/opt-out`, {
      method: 'DELETE',
    });
  },

  /**
   * POST /conecta/me/boost — activate a 7-day boost for 25 credits.
   * Implements SPEC §10.1: validates listing active + no existing boost
   * (409 BOOST_ALREADY_ACTIVE) + sufficient credits (400 INSUFFICIENT_
   * CREDITS). REUSES atomicallyDebitCredits from SPEC-081 on the BE
   * (anti-P-382 — NO parallel credit mechanism). Returns 200 with the
   * updated listing detail (boost sub-schema set: isActive=true,
   * expiresAt=now+7d, creditsSpent=25, paymentTxId=UnifiedTransaction id).
   *
   * Errors (thrown as `ApiClientError`):
   *   - 401 AUTH_TOKEN_INVALID (not authed)
   *   - 404 LISTING_NOT_FOUND (no listing or not active — anti-enumeration)
   *   - 409 BOOST_ALREADY_ACTIVE (boost.isActive=true with future expiresAt)
   *   - 400 INSUFFICIENT_CREDITS (balance < 25, with `details.currentBalance`
   *     + `details.required` + `details.redirectTo='/app/creditos'`)
   *   - 429 RATE_LIMIT_EXCEEDED (3/hr/user cap exceeded — anti-fraud §14.4)
   *
   * On 200, returns the updated listing detail view.
   */
  async boost(): Promise<ConectaListingDetail> {
    return apiFetch<ConectaListingDetail>(`/conecta/me/boost`, {
      method: 'POST',
    });
  },

  /**
   * POST /conecta/categories/suggest — suggest a new category (admin
   * approval queue). Creates a DirectoryCategory with status='pending'.
   * Rate-limited 3/day/user per SPEC §14.3 — BE returns 429 if exceeded.
   *
   * On 201, returns the created category suggestion (categoryId + slug +
   * status='pending' + message).
   */
  async suggestCategory(
    body: SuggestCategoryRequest,
  ): Promise<SuggestCategoryResponse> {
    return apiFetch<SuggestCategoryResponse>(`/conecta/categories/suggest`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /**
   * POST /conecta/listings/:username/reviews — create a review (PR5).
   * Implements SPEC §11.2 flow: hybrid bad-words filter (local list +
   * AI async) + 1-per-user-per-listing anti-spam. If the local wordlist
   * flags the text, the review is created with `status='pending_ai_review'`
   * + the AI runs async to either publish, hold for admin, or auto-hide.
   *
   * Returns the created review (with status='published' or
   * 'pending_ai_review').
   */
  async createReview(
    username: string,
    body: CreateReviewRequest,
  ): Promise<ConectaReviewView> {
    return apiFetch<ConectaReviewView>(
      `/conecta/listings/${encodeURIComponent(username)}/reviews`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    );
  },

  /**
   * PATCH /conecta/reviews/:reviewId — edit own review (PR5). Re-triggers
   * bad-words filter on the new text. SPEC §11.3: only the reviewer can
   * edit (BE returns 403 REVIEW_NOT_OWNER if the caller is not the owner).
   */
  async updateReview(
    reviewId: string,
    body: UpdateReviewRequest,
  ): Promise<ConectaReviewView> {
    return apiFetch<ConectaReviewView>(
      `/conecta/reviews/${encodeURIComponent(reviewId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(body),
      },
    );
  },

  /**
   * DELETE /conecta/reviews/:reviewId — soft-delete own review (PR5).
   * SPEC §11.3: only the reviewer can delete. The review is NOT hard-
   * deleted — it's marked `status='hidden_by_admin'` with
   * `hiddenByAdminId='self'` + `hiddenReason='user deleted'` for audit.
   */
  async deleteReview(reviewId: string): Promise<DeleteReviewResponse> {
    return apiFetch<DeleteReviewResponse>(
      `/conecta/reviews/${encodeURIComponent(reviewId)}`,
      {
        method: 'DELETE',
      },
    );
  },

  /**
   * GET /conecta/listings/:username/my-review — get the authed user's own
   * review for a given listing. Returns null if the user hasn't reviewed
   * this listing yet. Used by the FE to show edit/delete buttons instead
   * of the create form (anti-spam: 1 review per user per listing).
   *
   * Anti-P-382 fix (2026-10-28): pre-fix the FE had no way to know if the
   * user already reviewed → form always showed → spam attempts.
   */
  async getMyReview(username: string): Promise<ConectaReview | null> {
    try {
      return await apiFetch<ConectaReview>(
        `/conecta/listings/${encodeURIComponent(username)}/my-review`,
      );
    } catch (err) {
      // 404 = listing not found → no review. Other errors rethrow.
      if (err instanceof ApiClientError && err.status === 404) return null;
      throw err;
    }
  },
};

// ─── Admin namespace (PR7) ──────────────────────────────────────────────────
// `conectaAdmin` is exported from `./conecta-admin.ts` (extracted per
// anti-P-383 — `conecta.ts` was 495L + 200L of admin namespace = 696L >
// target). Mirrors the SPEC-244 IA17 split precedent (`admin-market-reviews.ts`
// extracted from `admin-market.ts`). The barrel `api-client/index.ts`
// re-exports it for the admin dashboard.
// TASK-FEAT-CONECTA-ADMIN-CREATE-CATEGORIES (2026-10-26): added
// AdminCreateCategoryBody + AdminDeleteCategoryResponse types.
export type {
  ConectaAdminPendingCategory,
  ConectaPendingCategoriesResponse,
  ConectaAdminCategory,
  ConectaAdminReport,
  ConectaAdminReportsResponse,
  ResolveReportBody,
  AdminHideListingBody,
  AdminRejectCategoryBody,
  ConectaAdminListingView,
  AdminCreateCategoryBody,
  AdminDeleteCategoryResponse,
} from './conecta-admin';
export { conectaAdmin } from './conecta-admin';
